import { ArtifactsService } from '@inference.engine/api-client/web';
import { useQuery } from '@tanstack/react-query';
import { inferenceHttp } from '@/lib/api/http-client';
import { getFileDownloadUrl } from '@/shared/api/assets.api';

/**
 * Which asset of an artifact to resolve a signed URL for:
 * - `thumbnail` — the artifact's `thumbnailAssetId` (image preview).
 * - `file` — the artifact's top-level `data.assetId` (the backing file, e.g. a PDF).
 */
export type ArtifactAssetSource = 'thumbnail' | 'file';

/**
 * Resolves a fresh, signed URL for an artifact's asset from its id, the same way
 * attached files resolve a download URL. The artifact lives in the inference-api's
 * own store; we read it by id and sign the requested asset via the assets API. A
 * `fallbackUrl` (e.g. the picker's already-signed thumbnail) short-circuits the
 * lookup. Returns undefined until resolved, or when the ids are missing.
 */
export function useArtifactAssetUrl(
  workspaceId: string | null | undefined,
  artifactId: string | null | undefined,
  source: ArtifactAssetSource,
  fallbackUrl?: string,
): string | undefined {
  const { data } = useQuery({
    queryKey: ['artifact-asset-url', workspaceId, artifactId, source],
    queryFn: async (): Promise<string | null> => {
      const response = await ArtifactsService.getWorkspaceArtifactById({
        client: inferenceHttp,
        path: { workspaceId: workspaceId as string, artifactId: artifactId as string },
      });
      const artifact = response.data;
      const assetId =
        source === 'thumbnail'
          ? artifact?.thumbnailAssetId
          : (artifact?.data as { assetId?: string } | undefined)?.assetId;
      if (!assetId) return null;
      return getFileDownloadUrl(assetId);
    },
    // A picker thumbnail (fallbackUrl) is already signed, so skip the round-trip.
    enabled: !fallbackUrl && Boolean(workspaceId) && Boolean(artifactId),
    // Signed URLs are short-lived but the asset id is stable; cache briefly to
    // dedupe repeated renders of the same chip without serving stale links.
    staleTime: 5 * 60 * 1000,
  });

  return fallbackUrl ?? data ?? undefined;
}
